import { IColumn, IDetailsRowProps } from '@fluentui/react';
import {
  CommonStyles,
  debounce,
  defaultSemanticColors,
  detailsHeaderStylesBasic,
  DetailsList,
  detailsListStylesBasic,
  detailsListStylesCard,
  EmptyMessage,
  FontWeights,
  getItemsWithEmptyItemsOfNextPage,
  highlightRowBorder,
  IColumnWithDataType,
  IDetailsListProps,
  Link,
  linkStylesGhost,
  ListRow,
  listRowStylesThin,
  Loader,
  loaderStylesSpinnerXLarge,
  RouterLink,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import { FeatureStoreService } from '../../featurestore/featurestoreApi';
import {
  ActivePermission,
  CoreService,
  type FeatureSet,
  type FeatureSetResponse,
  type FeatureSetsDerivedFromResponse,
  type FeatureSetSearchRequest,
  FeatureSetSearchRequestFeatureSetSortField,
  type FeatureSetSearchResults,
  type ListableFeatureSet,
  type Project,
  SortDirection,
  type VersionedId,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import {
  activePermissionNameMap,
  CommonImageSrc,
  ContentsTitle,
  defaultPageSize,
  SortInfo,
} from '../../featurestore/models';
import {
  convertFeatureSetToListableFeatureSet,
  getErrorMessageBarMessage,
  hasAPIError,
} from '../../featurestore/utils';
import { RoutePaths } from '../../pages/Routes';
import { formatDefaultDateAndTimeWithSeconds } from '../../utils/utils';
import { ListCellImage } from '../ListCellImage/ListCellImage';
import { RequestPermissionPanel } from '../RequestPermissionPanel/RequestPermissionPanel';
import { featureSetListStylesDefault, IFeatureSetListClassNames, IFeatureSetListStyles } from './FeatureSetList.styles';

const getColumns = (
  isCardView: boolean,
  onRequestAccess: (id: string, name: string) => void,
  onColumnClick: (e: React.MouseEvent<HTMLElement, MouseEvent>, col: IColumn) => void,
  sortColumn?: IColumn,
  readOnly?: boolean,
  onView?: (item: ListableFeatureSet) => void
): IColumnWithDataType[] => {
  const columns: IColumnWithDataType[] = [];
  if (isCardView) {
    columns.push({
      key: 'featureSetName',
      name: 'Feature Set Name',
      fieldName: 'description',
      minWidth: 200,
      data: {
        headerFieldName: 'name',
        listCellProps: {
          emptyMessage: 'No Description',
          onRenderPrefix: (item: ListableFeatureSet) =>
            item.name?.toLowerCase().includes('h2o') ? (
              <ListCellImage imageSrc={CommonImageSrc.logo512} />
            ) : (
              <ListCellImage iconName="FormLibraryMirrored" />
            ),
        },
      },
    });
  } else {
    columns.push(
      {
        key: 'name',
        name: 'Feature Set Name',
        fieldName: 'name',
        minWidth: 100,
        maxWidth: 150,
        headerClassName: 'is-sortable',
        iconName: 'Sort',
        onColumnClick,
      },
      {
        key: 'description',
        name: 'Description',
        fieldName: 'description',
        minWidth: 200,
        data: {
          listCellProps: { emptyMessage: 'No Description' },
        },
        headerClassName: 'is-sortable',
        iconName: 'Sort',
        onColumnClick,
      }
    );
  }

  columns.push(
    {
      key: 'projectName',
      name: 'Project Name',
      fieldName: 'projectName',
      minWidth: 150,
      headerClassName: 'is-sortable',
      iconName: 'Sort',
      onColumnClick,
    },
    {
      key: 'author',
      name: 'Author',
      fieldName: 'authorName',
      minWidth: 120,
      headerClassName: 'is-sortable',
      iconName: 'Sort',
      onColumnClick,
    },
    {
      key: 'lastUpdateDateTime',
      name: 'Updated On',
      fieldName: 'lastUpdateDateTime',
      minWidth: 190,
      data: {
        listCellProps: {
          onRenderText: (item: FeatureSet) => (
            <>{formatDefaultDateAndTimeWithSeconds(item.lastUpdateDateTime) || `-`}</>
          ),
        },
      },
      headerClassName: 'is-sortable',
      iconName: 'Sort',
      onColumnClick,
    },
    ...(readOnly
      ? []
      : [
          {
            key: 'AccessType',
            name: 'Access Type',
            fieldName: 'permission',
            minWidth: 120,
            maxWidth: 120,
            onRender: (item: ListableFeatureSet) => {
              return item.permission !== ActivePermission.ACTIVE_PERMISSION_NONE ? (
                <div style={{ ...(CommonStyles.flexCenterVertically as CSSProperties) }}>
                  {isCardView && (
                    <div
                      style={{
                        fontWeight: FontWeights.semiBold,
                        paddingBottom: 6,
                      }}
                    >
                      Access Type
                    </div>
                  )}
                  <div
                    style={{
                      height: 18,
                      color: defaultSemanticColors.textSecondary,
                      ...(CommonStyles.truncateString as CSSProperties),
                    }}
                  >
                    {activePermissionNameMap.get(item.permission)}
                  </div>
                </div>
              ) : (
                <div style={{ ...(CommonStyles.flexCenterVertically as CSSProperties) }}>
                  <Link onClick={() => onRequestAccess(item.id || '', item.name || '')}>Request Access</Link>
                </div>
              );
            },
          },
        ]),
    {
      key: 'view',
      name: 'View',
      minWidth: 120,
      onRender: (item: ListableFeatureSet) => {
        const { id } = item;
        return (
          <div
            style={{
              ...(CommonStyles.flexCenterVertically as CSSProperties),
              width: '100%',
              textAlign: 'right',
            }}
          >
            <div>
              {onView ? (
                <Link styles={linkStylesGhost} onClick={() => onView(item)} data-test="view-feature-set-button">
                  View
                </Link>
              ) : (
                <RouterLink styles={linkStylesGhost} to={`${RoutePaths.FEATURESTORE_FEATURE_SET}/${id}`}>
                  View
                </RouterLink>
              )}
            </div>
          </div>
        );
      },
    }
  );
  if (sortColumn) {
    for (let i = 0; i < columns.length; i++) {
      if (sortColumn.key === columns[i].key) {
        columns[i] = { ...columns[i], ...sortColumn };
        break;
      }
    }
  }
  return columns;
};

const fieldSortTypeMap = new Map([
  ['name', FeatureSetSearchRequestFeatureSetSortField.FEATURE_SET_SORT_FIELD_NAME],
  ['description', FeatureSetSearchRequestFeatureSetSortField.FEATURE_SET_SORT_FIELD_DESCRIPTION],
  ['author', FeatureSetSearchRequestFeatureSetSortField.FEATURE_SET_SORT_FIELD_AUTHOR],
  ['lastUpdateDateTime', FeatureSetSearchRequestFeatureSetSortField.FEATURE_SET_SORT_FIELD_LAST_UPDATE],
  ['projectName', FeatureSetSearchRequestFeatureSetSortField.FEATURE_SET_SORT_FIELD_PROJECT_NAME],
]);

interface FeatureSetWithInfo extends ListableFeatureSet {
  authorName?: string;
  name?: string;
  projectName?: string;
  isNotLoaded?: boolean;
}

export interface FeatureSetListData {
  items: FeatureSetWithInfo[];
  pageToken?: string;
  pageSize?: number;
}

const convertFeatureSetToFeatureSetWithUserNames = (item: ListableFeatureSet): FeatureSetWithInfo => ({
  ...item,
  authorName: item.author?.name,
});

interface IFeatureSetListProps {
  parentVersionIds?: VersionedId[];
  childVersionId?: VersionedId;
  ownedOnly?: boolean;
  isCardView?: boolean;
  searchText?: string;
  project?: Project;
  reload?: boolean;
  sortInfo?: SortInfo;
  onUpdateData?: (count: number, pageSize: number, pageToken: string, name: ContentsTitle) => void;
  onScroll?: React.UIEventHandler<HTMLDivElement>;
  onView?: (item: ListableFeatureSet) => void;
}

export const FeatureSetList = ({
  ownedOnly,
  isCardView = true,
  searchText,
  project,
  reload,
  sortInfo,
  childVersionId,
  parentVersionIds,
  onUpdateData,
  onScroll,
  onView,
}: IFeatureSetListProps) => {
  const [data, setData] = useState<FeatureSetListData>({
    items: [],
  });
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [showRequestAccessPanel, setShowRequestAccessPanel] = useState(false);
  const refMeta = useRef<{
    sortInfo?: SortInfo;
    sortColumn?: IColumn;
    columns?: IColumn[];
    selectedIdName: { id: string; name: string };
    isLoadingPage: boolean;
    pageSize: number;
    mounted?: boolean;
  }>({
    selectedIdName: { id: '', name: '' },
    isLoadingPage: false,
    pageSize: defaultPageSize,
  });
  const { addToast } = useToast();
  const load = useCallback(
    async ({ items, pageToken }: FeatureSetListData = { items: [] }, hasLoading = true) => {
      hasLoading && setLoadingMessage(`Loading feature sets...`);
      try {
        const pageSize = refMeta.current.pageSize;
        const sort = refMeta.current.sortInfo;
        const hasSort =
          sort &&
          sort.field &&
          sort.field !== FeatureSetSearchRequestFeatureSetSortField.FEATURE_SET_SORT_FIELD_UNDEFINED;
        const req: FeatureSetSearchRequest = {
          query: searchText,
          pageSize,
          pageToken,
          ownedOnly,
          sortField: hasSort ? (sort?.field as FeatureSetSearchRequestFeatureSetSortField) : undefined,
          sortDirection: hasSort ? sort?.direction : undefined,
        };
        if (project?.id) {
          req.projectIds = [project.id];
        }
        let token = '';
        let newItems: FeatureSetWithInfo[] = [];
        if (childVersionId) {
          const { id, majorVersion } = childVersionId;
          if (id && majorVersion) {
            const { listableFeatureSets = [] }: FeatureSetsDerivedFromResponse = await fsApi(
              FeatureStoreService.GetFeatureSetsDerivedFrom,
              { featureSetId: id, featureSetMajorVersion: majorVersion }
            );
            newItems = listableFeatureSets.map((item) => convertFeatureSetToFeatureSetWithUserNames(item));
          }
        } else if (parentVersionIds) {
          const apis = parentVersionIds.map(({ id: featureSetId, majorVersion }) =>
            fsApi(CoreService.GetFeatureSetsLastMinorForCurrentMajor, {
              featureSetId,
              featureSetVersion: majorVersion ? majorVersion.toFixed(1) : undefined,
            })
          );
          if (apis.length) {
            const results: FeatureSetResponse[] = await Promise.all(apis);
            results.forEach((d) => {
              if (hasAPIError(d)) {
                addToast(getErrorMessageBarMessage(d, `An error has occurred while loading Feature Set: `));
              } else if (d.featureSet) {
                newItems.push(
                  convertFeatureSetToFeatureSetWithUserNames(convertFeatureSetToListableFeatureSet(d.featureSet))
                );
              }
            });
          }
        } else {
          const { listableFeatureSet = [], nextPageToken = '' }: FeatureSetSearchResults = await fsApi(
            CoreService.SearchFeatureSets,
            req
          );
          token = nextPageToken;
          newItems = getItemsWithEmptyItemsOfNextPage(
            items,
            listableFeatureSet,
            pageSize,
            convertFeatureSetToFeatureSetWithUserNames
          );
        }
        const newData = {
          items: newItems,
          pageToken: token,
          pageSize: refMeta.current.pageSize,
        };
        if (!refMeta.current.mounted) return;
        setData({
          ...newData,
        });
        onUpdateData &&
          onUpdateData(newData.items.length, newData.pageSize, newData.pageToken, ContentsTitle.featureSets);
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while loading feature sets: `));
      } finally {
        if (!refMeta.current.mounted) return;
        if (hasLoading) setLoadingMessage('');
        refMeta.current.isLoadingPage = false;
      }
    },
    [addToast, ownedOnly, searchText, onUpdateData]
  );
  const onLoad = useMemo(() => debounce(300, load), [load]);
  const onRequestAccess = useCallback((id: string, name: string) => {
    refMeta.current.selectedIdName = { id, name };
    setShowRequestAccessPanel(true);
  }, []);
  const onColumnClick = useCallback(
    (_e, col) => {
      const column = refMeta.current.columns?.find((c: IColumn) => c.key === col.key);
      if (column) {
        if (!column.isSorted) {
          column.isSorted = true;
          column.isSortedDescending = false;
        } else if (!column.isSortedDescending) {
          column.isSortedDescending = true;
        } else {
          column.isSorted = false;
        }
        refMeta.current.sortInfo = column.isSorted
          ? {
              field: fieldSortTypeMap.get(column.key),
              direction: column.isSortedDescending ? SortDirection.SORT_DESC : SortDirection.SORT_ASC,
            }
          : undefined;
      }
      refMeta.current.sortColumn = column ? { ...column } : undefined;
      load();
    },
    [load]
  );
  refMeta.current.columns = getColumns(
    isCardView,
    onRequestAccess,
    onColumnClick,
    refMeta.current.sortColumn,
    !!childVersionId || !!parentVersionIds,
    onView
  );
  const detailsListProps: IDetailsListProps = {
    styles: isCardView ? detailsListStylesCard : detailsListStylesBasic,
    headerStyles: detailsHeaderStylesBasic,
    columns: refMeta.current.columns,
    isHeaderVisible: !isCardView,
    items: data.items,
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: 0,
    },
    onRenderRow: (props?: IDetailsRowProps) => {
      const { item, columns = [] } = props || {};
      if (item.isNotLoaded && !refMeta.current.isLoadingPage) {
        refMeta.current.isLoadingPage = true;
        onLoad(data, false);
      }
      return (
        <ListRow
          styles={isCardView ? undefined : listRowStylesThin}
          hideHeader={!isCardView}
          data={item}
          columns={columns}
          loading={item.isNotLoaded}
          onMouseEnter={isCardView ? undefined : (e) => highlightRowBorder(e)}
          onMouseLeave={isCardView ? undefined : (e) => highlightRowBorder(e, false)}
        />
      );
    },
  };
  useEffect(() => {
    refMeta.current.sortInfo = { ...sortInfo };
    refMeta.current.mounted = true;
    load();
    return () => {
      refMeta.current.mounted = false;
    };
  }, [load, sortInfo]);
  useEffect(() => {
    reload && load();
  }, [reload]);
  const classNames = useClassNames<IFeatureSetListStyles, IFeatureSetListClassNames>(
    'FeatureSetList',
    featureSetListStylesDefault
  );
  return (
    <div className={classNames.root} data-test="FeatureSetList">
      {loadingMessage ? (
        <Loader styles={loaderStylesSpinnerXLarge} label={loadingMessage} />
      ) : isCardView ? (
        <div
          className={classNames.cardView}
          data-is-scrollable="true"
          onScroll={onScroll}
          data-test="details-list-card-view"
        >
          {data.items.length === 0 ? (
            <EmptyMessage message="No Feature Sets Available" />
          ) : (
            <DetailsList {...detailsListProps} />
          )}
        </div>
      ) : (
        <div className={classNames.listView}>
          <div
            className={classNames.listViewWrap}
            data-is-scrollable="true"
            onScroll={onScroll}
            data-test="details-list-list-view"
          >
            {data.items.length === 0 ? (
              <EmptyMessage message="No Feature Sets Available" />
            ) : (
              <DetailsList {...detailsListProps} />
            )}
          </div>
        </div>
      )}
      <RequestPermissionPanel
        featureSetId={refMeta.current.selectedIdName.id}
        name={refMeta.current.selectedIdName.name}
        show={showRequestAccessPanel}
        onDismiss={(_e, updated) => {
          if (updated) {
            load();
          }
          setShowRequestAccessPanel(false);
        }}
      />
    </div>
  );
};
